/**
 * Connecting a client or matter to a QuickBooks customer.
 *
 * ⚠ The prototypes still carry their own copy of this logic, because the other five consumers
 * (trust.matter.ctrl.js ×2, trust.request.ctrl.js, invoice.editor.ctrl.js, billing/trust/
 * trust.matter.ctrl.js) still call them. Until those migrate and the prototypes are deleted or
 * made to delegate here, any change to the connect rules has to be made in BOTH places.
 *
 * The flow, in the order the original ran it:
 *   1. Already linked?                       -> return the existing link, no requests.
 *   2. QuickBooks not integrated at all      -> { connect: false } (nothing to offer the user).
 *   3. Firm lets users pick the customer     -> { connect: true } (caller routes to the picker).
 *   4. Otherwise create/link the customer    -> return the new link, or { connect: true } on failure.
 */
import axios from 'axios';

/** Loose on purpose: client/matter records are untyped across this codebase. */
type Record_ = Record<string, any>;

export interface AccountingLink {
  matter?: Record_ | null;
  client?: Record_ | null;
  trust?: Record_ | null;
  /** true = the caller must send the user to the QuickBooks customer picker. */
  connect?: boolean;
}

// ── cache invalidation ───────────────────────────────────────────────────
//
// Connecting a record changes data that two AngularJS-side caches hold: clientMatterManager's
// client/matter maps and accountingService's QuickBooks customer list. Neither is reachable from
// a Vue module, so they register here instead and we notify them after a successful write.

type ChangeScope = { clients: boolean; matters: boolean };
const listeners = new Set<(scope: ChangeScope) => void>();

export function onAccountingChanged(fn: (scope: ChangeScope) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notifyChanged(scope: ChangeScope): void {
  listeners.forEach((fn) => fn(scope));
}

// ── transport ────────────────────────────────────────────────────────────

/** Rejects with `{ enabled: false }` when the firm has no QuickBooks integration at all. */
async function verifyQuickBooks(me: Record_): Promise<Record_> {
  if (!me?.company?.integration?.quickbooks) {
    throw { enabled: false };
  }
  const { data: status } = await axios({ method: 'GET', url: '/accounting' });
  if (!status.connected) {
    throw status;
  }
  return status;
}

async function putClientAccounting(id: string, name: string, billClient?: boolean) {
  const { data } = await axios({
    method: 'PUT',
    url: `/clients/${id}/accounting`,
    data: { accounting: { name }, billClient },
  });
  return data;
}

async function putMatterAccounting(id: string, name: string) {
  const { data } = await axios({
    method: 'PUT',
    url: `/matters/${id}/accounting`,
    data: { accounting: { name } },
  });
  return data;
}

// ── shared helpers ───────────────────────────────────────────────────────

function linkFor(matter: Record_ | null, client?: Record_ | null): AccountingLink {
  return {
    matter: matter ? matter.accounting : null,
    client: client ? client.accounting : matter?.client?.accounting,
    trust: matter ? matter.billing?.trust : client?.billing?.trust,
  };
}

/**
 * Writes are applied onto the passed record in place. The matter flow depends on it: it links the
 * client first, then reads `matter.client.accounting` back when linking the matter itself.
 */
async function linkClient(client: Record_, billClient?: boolean): Promise<AccountingLink> {
  const updated = await putClientAccounting(client.id, client.label, billClient);
  notifyChanged({ clients: true, matters: false });
  client.accounting = updated.accounting;
  client.billing = updated.billing;
  return linkFor(null, client);
}

async function linkMatter(matter: Record_): Promise<AccountingLink> {
  try {
    const updated = await putMatterAccounting(matter.id, matter.label);
    notifyChanged({ clients: false, matters: true });
    Object.assign(matter, updated);
    return linkFor(matter);
  } catch {
    return { connect: true };
  }
}

/** Both flows treat "QuickBooks isn't set up" and "QuickBooks is down" differently. */
function onVerifyFailure(status: Record_): AccountingLink {
  if (!status?.enabled) {
    return { connect: false };
  }
  throw new Error('QuickBooks is not connected');
}

// ── public API ───────────────────────────────────────────────────────────

export async function getOrConnectClient(
  client: Record_,
  me: Record_,
): Promise<AccountingLink> {
  if (client.accounting) {
    return { client: client.accounting, trust: client.billing?.trust };
  }
  try {
    await verifyQuickBooks(me);
  } catch (status) {
    return onVerifyFailure(status as Record_);
  }
  const clientBilling = me.company.settings.clientBilling || {};
  if (clientBilling.allowChange) {
    return { connect: true };
  }
  try {
    return await linkClient(client, clientBilling.billClient);
  } catch {
    return { connect: true };
  }
}

export async function getOrConnectMatter(
  matter: Record_,
  me: Record_,
): Promise<AccountingLink> {
  if (matter.accounting) {
    return linkFor(matter);
  }
  if (matter.client?.billing?.billClient && matter.client?.accounting) {
    return linkFor(null, matter.client);
  }
  try {
    await verifyQuickBooks(me);
  } catch (status) {
    return onVerifyFailure(status as Record_);
  }
  if (matter.client?.accounting) {
    return linkMatter(matter);
  }
  const clientBilling = me.company.settings.clientBilling || {};
  if (clientBilling.allowChange) {
    return { connect: true };
  }
  if (clientBilling.billClient && matter.client?.billing?.billClient) {
    // Billing at client level: linking the client is the whole job, the matter rides on it.
    try {
      return await linkClient(matter.client, true);
    } catch {
      return { connect: true };
    }
  }
  await linkClient(matter.client);
  return linkMatter(matter);
}
